import { useState } from "react";
import Collapse from "../../../components/Collapse";
import { formatBook as fmtBook } from "../../../utils/formatOdds";

/**
 * V VIP Teenpatti 1-day (teen62) bet table — reference markup (.teen1daycasino-container):
 * Player A / Player B Main + Consecutive (Back/Lay, book, range popup) and the Card 1-6 Odd/Even grid.
 * Suspended = gstatus !== "OPEN"; book is green when > 0, otherwise red (reference v-casinobook).
 * Styled by the ported reference CSS (index.css); bet clicks keep the page's onBetClick(value, selection, item, type).
 */

// Normalize string for matching
const normalizeString = (str) => {
  if (!str) return "";
  return str
    .replace(/\s*\(\s*/g, "(")
    .replace(/\s*\)\s*/g, ")")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
};

// Reference v-kvalmin / v-kvalmax: 1000 → 1K, 100000 → 1L
const kval = (n) => {
  const v = Number(Number(n || 0).toFixed(2));
  if (v >= 1e3 && v < 1e5) return `${parseFloat((v / 1e3).toFixed(2))}K`;
  if (v >= 1e5) return `${parseFloat((v / 1e5).toFixed(2))}L`;
  return parseFloat(v);
};

const bySr = (a, b) => (a?.sr ?? 0) - (b?.sr ?? 0);

const BetTableTeenPatti62 = ({ data = [], onBetClick, exposures = {}, myBets = [] }) => {
  const [openRanges, setOpenRanges] = useState({});
  const toggleRange = (id) => setOpenRanges((prev) => ({ ...prev, [id]: !prev[id] }));

  // Exposure lookup (all key variations, then normalized match)
  const getExposure = (betName) => {
    if (!betName) return 0;
    const keys = [betName, betName.toLowerCase(), betName.toLowerCase().trim(), betName.trim(), normalizeString(betName)];
    for (const key of keys) {
      if (key && exposures[key] !== undefined && exposures[key] !== null) {
        const v = parseFloat(exposures[key]);
        if (!isNaN(v)) return v;
      }
    }
    const nk = normalizeString(betName);
    for (const expKey in exposures) {
      if (normalizeString(expKey) === nk) {
        const v = parseFloat(exposures[expKey]);
        if (!isNaN(v)) return v;
      }
    }
    return 0;
  };

  const main = data.filter((i) => i.subtype === "teen").sort(bySr);
  const consecutive = data.filter((i) => i.subtype === "con").sort(bySr);
  const oddEven = data
    .filter((i) => i.subtype === "oddeven")
    .sort(bySr)
    .map((c) => ({ ...c, odds: Array.isArray(c.odds) ? [...c.odds].sort((a, b) => (a?.sno ?? 0) - (b?.sno ?? 0)) : [] }));

  const playerA_Main = main.find((i) => i.nat === "Player A") || main[0] || {};
  const playerB_Main = main.find((i) => i.nat === "Player B") || main[1] || {};
  const playerA_Consec = consecutive.find((i) => i.nat === "Player A") || consecutive[0] || {};
  const playerB_Consec = consecutive.find((i) => i.nat === "Player B") || consecutive[1] || {};

  // Book for a Main/Consecutive row: exposure of this player's back/lay bet (same matching as before)
  const rowBook = (label, item, playerLabel) => {
    const normalizedPlayerLabel = normalizeString(playerLabel);
    const itemNat = normalizeString(item?.nat || "");
    const isPlayerA = itemNat.includes("player a") || normalizedPlayerLabel.includes("player a");
    const isPlayerB = itemNat.includes("player b") || normalizedPlayerLabel.includes("player b");
    const matchesPlayer = () => {
      if (isPlayerA) return itemNat.includes("player a") && !itemNat.includes("player b");
      if (isPlayerB) return itemNat.includes("player b") && !itemNat.includes("player a");
      return false;
    };
    const findBet = (type) => {
      const mainSel = normalizeString(`${label} ${type === "back" ? "Back" : "Lay"}`);
      const playerSel = normalizeString(`${playerLabel} ${label} ${type === "back" ? "Back" : "Lay"}`);
      return myBets.find((bet) => {
        const sel = normalizeString(bet.selection || bet.matchedBet || bet.player_name || "");
        if ((bet.type || bet.betType) !== type) return false;
        if (sel === normalizedPlayerLabel || sel === mainSel) return matchesPlayer();
        if (sel === playerSel || sel.includes(normalizedPlayerLabel)) return sel.includes(mainSel);
        return false;
      });
    };
    const expOf = (bet, type) => {
      if (!bet) return 0;
      if (bet.exposer !== undefined && bet.exposer !== null && bet.exposer !== 0) return bet.exposer;
      const T = type === "back" ? "Back" : "Lay";
      const names = [
        bet.selection || bet.matchedBet || bet.player_name,
        `${playerLabel} ${label} ${T}`,
        `${item?.nat || ""} ${T}`,
        item?.nat || `${playerLabel} ${label} ${T}`,
        playerLabel,
        normalizeString(bet.selection || bet.matchedBet || bet.player_name || ""),
      ].filter(Boolean);
      for (const n of names) {
        const e = getExposure(n);
        if (e) return e;
      }
      return 0;
    };
    return expOf(findBet("back"), "back") || expOf(findBet("lay"), "lay") || 0;
  };

  const cardBook = (cardName, cardType) => {
    const selectionName = `${cardName} ${cardType}`;
    const ns = normalizeString(selectionName);
    const bet = myBets.find((b) => {
      const sel = normalizeString(b.selection || b.matchedBet || b.player_name || "");
      return sel === ns || sel.includes(ns) || (sel.includes(normalizeString(cardType)) && sel.includes(normalizeString(cardName)));
    });
    if (!bet) return 0;
    if (bet.exposer !== undefined && bet.exposer !== null && bet.exposer !== 0 && !isNaN(bet.exposer)) return bet.exposer;
    const names = [bet.selection || bet.matchedBet || bet.player_name, selectionName].filter(Boolean);
    for (const n of names) {
      const e = getExposure(n);
      if (e) return e;
    }
    return getExposure(cardType) || 0;
  };

  const bookClass = (v) => (Number(v) > 0 ? "book-green" : "book-red");

  const rangeBox = (id, item, collapsedIcon = true) => (
    <>
      <i
        className={collapsedIcon && !openRanges[id] ? "fas fa-info-circle collapsed" : "fas fa-info-circle"}
        data-toggle="collapse"
        data-target={`#${id}`}
        aria-expanded={collapsedIcon ? (openRanges[id] ? "true" : "false") : undefined}
        onClick={() => toggleRange(id)}
      />{" "}
      <Collapse id={id} open={!!openRanges[id]} className="icon-range">
        R:<span>{kval(item?.min)}</span>-<span>{kval(item?.max)}</span>
      </Collapse>
    </>
  );

  const renderPlayerRow = (label, item, playerLabel, rangeId) => {
    const suspended = item?.gstatus !== "OPEN";
    const book = rowBook(label, item, playerLabel);
    return (
      <div className="casino-box-row">
        <div className={label === "Consecutive" ? "casino-nation-name casino-card-img" : "casino-nation-name"}>
          <b>{label}</b>{" "}
          <div className="float-right">
            <span className={`mr-2 casino-book ${bookClass(book)}`}>{fmtBook(book)}</span>{" "}
            {rangeBox(rangeId, item)}
          </div>
        </div>{" "}
        <div className="casino-bl-box">
          <div
            className={suspended ? "back casino-bl-box-item suspended" : "back casino-bl-box-item"}
            onClick={() => !suspended && item?.b && onBetClick(item.b, item?.nat || `${playerLabel} ${label} Back`, item, "back")}
          >
            <span className="casino-box-odd">{item?.b ?? 0}</span>
          </div>{" "}
          <div
            className={suspended ? "lay casino-bl-box-item suspended" : "lay casino-bl-box-item"}
            onClick={() => !suspended && item?.l && onBetClick(item.l, item?.nat || `${playerLabel} ${label} Lay`, item, "lay")}
          >
            <span className="casino-box-odd">{item?.l ?? 0}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderCardCells = (cardType) =>
    oddEven.map((card, i) => {
      const odd = card.odds.find((o) => String(o?.nat || "").toLowerCase() === cardType.toLowerCase());
      const suspended = card.gstatus !== "OPEN";
      const book = cardBook(card.nat, cardType);
      const selectionName = `${card.nat} ${cardType}`;
      const cell = odd ? (
        <div
          className={suspended ? "back casino-bl-box-item suspended" : "back casino-bl-box-item"}
          onClick={() => !suspended && odd.b && onBetClick(odd.b, selectionName, { ...odd, gstatus: card.gstatus })}
        >
          <span className="casino-box-odd">{odd.b}</span>
        </div>
      ) : null;
      const bookEl = odd ? <div className={`casino-book ${bookClass(book)}`}>{fmtBook(book)}</div> : null;
      return (
        <div key={card.sid ?? i} className="casino-bl-box">
          {cell}
          {odd ? " " : null}
          {bookEl}
        </div>
      );
    });

  return (
    <div className="teen1daycasino-container d-none-small">
      <div className="teen1dayleft">
        <div className="casino-box-row">
          <div className="casino-nation-name no-border casino-bl-box-title">
            <div className="playera">Player A</div>
          </div>{" "}
          <div className="casino-bl-box casino-bl-box-title">
            <div className="casino-bl-box-item"><b>Back</b></div>{" "}
            <div className="casino-bl-box-item"><b>Lay</b></div>
          </div>
        </div>{" "}
        {renderPlayerRow("Main", playerA_Main, "Player A", "range1")}{" "}
        {renderPlayerRow("Consecutive", playerA_Consec, "Player A", "range6")}
      </div>{" "}
      <div className="teen1daycenter" />{" "}
      <div className="teen1dayright">
        <div className="casino-box-row">
          <div className="casino-nation-name no-border casino-bl-box-title">
            <div className="playerb">Player B</div>
          </div>{" "}
          <div className="casino-bl-box casino-bl-box-title">
            <div className="casino-bl-box-item"><b>Back</b></div>{" "}
            <div className="casino-bl-box-item"><b>Lay</b></div>
          </div>
        </div>{" "}
        {renderPlayerRow("Main", playerB_Main, "Player B", "range7")}{" "}
        {renderPlayerRow("Consecutive", playerB_Consec, "Player B", "range12")}
      </div>{" "}
      <div className="teen1dayother">
        <div className="casino-box-row">
          <div className="casino-nation-name no-border" />{" "}
          {[1, 2, 3, 4, 5, 6].map((n) => (
            <div key={n} className="casino-bl-box">
              <div className="casino-bl-box-item"><b>Card {n}</b></div>
            </div>
          ))}
        </div>{" "}
        <div className="casino-box-row mb-3">
          <div className="casino-nation-name mb-4">
            <b>Odd</b>{" "}
            <div className="float-right">{rangeBox("range13", oddEven[0], false)}</div>
          </div>{" "}
          {renderCardCells("Odd")}
        </div>{" "}
        <div className="casino-box-row">
          <div className="casino-nation-name mb-4">
            <b>Even</b>{" "}
            <div className="float-right">{rangeBox("range14", oddEven[0], false)}</div>
          </div>{" "}
          {renderCardCells("Even")}
        </div>
      </div>
    </div>
  );
};

export default BetTableTeenPatti62;
